import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FiStar, FiMinus, FiPlus, FiShare2, FiHeart } from 'react-icons/fi';
import api from '../utils/api';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import PincodeChecker from '../components/PincodeChecker';
import ImageLightbox from '../components/ImageLightbox';
import PageLoader from '../components/PageLoader';
import toast from 'react-hot-toast';
import { resolveImageUrl, primaryImageUrl, PLACEHOLDER } from '../utils/imageUrl';
import './ProductDetail.css';

function ProductDetail() {
  const { id } = useParams(); // This is actually the slug from the URL
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { isInWishlist, toggleWishlist } = useWishlist();

  const [product, setProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedColor, setSelectedColor] = useState('');
  const [selectedSizeVariant, setSelectedSizeVariant] = useState(null);
  const [addingToCart, setAddingToCart] = useState(false);
  const [buyingMode, setBuyingMode] = useState('meter'); // 'meter' | 'coil'
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const COLOR_MAP = {
    White: '#f9fafb', Black: '#111827', Grey: '#9ca3af', Silver: '#c0c0c0',
    Brown: '#92400e', Cream: '#fef3c7', Red: '#dc2626', Blue: '#2563eb',
    Yellow: '#fbbf24', Green: '#16a34a', Orange: '#ea580c', Gold: '#d97706',
  };

  // When the customer picks a colour, jump to the matching image (if tagged)
  useEffect(() => {
    if (!selectedColor || !product) return;
    const idx = (product.images || []).findIndex(
      (img) => img.colorTag === selectedColor
    );
    if (idx !== -1) setSelectedImage(idx);
  }, [selectedColor, product]);

  // Set default size and color on product load (independent selections)
  useEffect(() => {
    if (!product?.variants?.length) return;
    const defaultSize = product.variants.find(v => v.type === 'size' && v.isDefault)
      || product.variants.find(v => v.type === 'size');
    const defaultColor = product.variants.find(v => v.type === 'color' && v.isDefault)
      || product.variants.find(v => v.type === 'color');
    if (defaultSize) setSelectedSizeVariant(defaultSize);
    if (defaultColor) setSelectedColor(defaultColor.label);
    // If product only has one type of variant, handle it
    if (!defaultSize && !defaultColor) {
      const defaultV = product.variants.find(v => v.isDefault) || product.variants[0];
      if (defaultV?.type === 'size') setSelectedSizeVariant(defaultV);
      if (defaultV?.type === 'color') setSelectedColor(defaultV.label);
    }
  }, [product]);

  useEffect(() => {
    const fetchProduct = async () => {
      if (!id || id === 'undefined') {
        navigate('/shop');
        return;
      }
      try {
        setLoading(true);
        const res = await api.get(`/products/${id}`);
        setProduct(res.data.data);
        setSelectedImage(0);

        // Fetch related products by category
        if (res.data.data.category) {
          const relatedRes = await api.get(`/products?category=${res.data.data.category}&limit=4`);
          setRelatedProducts((relatedRes.data.data || []).filter(p => p._id !== res.data.data._id));
        }
      } catch (error) {
        console.error('Error fetching product:', error);
        toast.error('Product not found');
        navigate('/shop');
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id, navigate]);

  // Derive color variant from selected color
  const selectedColorVariant = selectedColor
    ? (product?.variants || []).find(v => v.type === 'color' && v.label === selectedColor)
    : null;

  const handleAddToCart = async () => {
    if (addingToCart) return;
    setAddingToCart(true);
    try {
      // Build compound variant with both size and color when applicable
      let variant = null;
      if (selectedSizeVariant || selectedColorVariant || selectedColor) {
        const parts = [selectedSizeVariant?.label, selectedColor].filter(Boolean);
        const isCompound = selectedSizeVariant && selectedColor;
        variant = {
          type: isCompound ? 'compound' : (selectedSizeVariant ? 'size' : 'color'),
          label: parts.join(' / '),
          sizeLabel: selectedSizeVariant?.label || null,
          colorLabel: selectedColor || null,
          sellingPrice: selectedSizeVariant?.sellingPrice || selectedColorVariant?.sellingPrice || product.sellingPrice,
          mrp: selectedSizeVariant?.mrp || selectedColorVariant?.mrp || product.mrp,
          sku: selectedSizeVariant?.sku || selectedColorVariant?.sku || null,
        };
      }

      // For wire products, pass total meters as quantity and annotate the variant
      const cartQty = actualQty;
      if (isWireProduct) {
        // Use effective per-meter rate: coil mode may have a cheaper per-meter price
        const perMeter = buyingMode === 'coil' ? coilPrice / coilLength : effectivePrice;
        const perMeterMrp = buyingMode === 'coil' ? coilMrpPrice / coilLength : effectiveMrp;
        variant = variant || { type: 'unit' };
        variant.sellingPrice = perMeter;
        variant.mrp = perMeterMrp;
        const modeLabel = buyingMode === 'coil'
          ? `${quantity} coil${quantity > 1 ? 's' : ''} (${cartQty}m)`
          : `${cartQty}m`;
        variant.label = variant.label ? `${variant.label} / ${modeLabel}` : modeLabel;
        variant.unit = 'meter';
      }

      // Pass wire options for correct stock pool deduction
      const wireOptions = isWireProduct ? {
        buyingMode,
        coilQuantity: buyingMode === 'coil' ? quantity : 0,
      } : null;
      await addToCart(product._id, cartQty, variant, wireOptions);
      if (isWireProduct) {
        toast.success(`${product.name} — ${cartQty}m added to cart!`);
      } else {
        const variantLabel = variant ? ` (${variant.label})` : '';
        toast.success(`${product.name}${variantLabel} added to cart!`);
      }
      setQuantity(1);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to add to cart');
    } finally {
      setAddingToCart(false);
    }
  };

  if (loading) {
    return (
      <div className="product-detail-page">
        <div className="page-header">
          <div className="container">
            <span className="pd-skel pd-skel-line" style={{ width: 160 }} />
          </div>
        </div>
        <div className="container product-detail-container">
          <div className="detail-grid">
            {/* Left — image skeleton */}
            <div className="detail-images">
              <div className="pd-skel pd-skel-image" />
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                {[1,2,3,4].map(i => <div key={i} className="pd-skel pd-skel-thumb" />)}
              </div>
            </div>
            {/* Right — info skeleton */}
            <div className="detail-info" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div className="pd-skel pd-skel-line" style={{ width: '40%', height: 14 }} />
              <div className="pd-skel pd-skel-line" style={{ width: '85%', height: 28 }} />
              <div className="pd-skel pd-skel-line" style={{ width: '60%', height: 22 }} />
              <div className="pd-skel pd-skel-line" style={{ width: '100%', height: 48, borderRadius: 12 }} />
              <div style={{ display: 'flex', gap: '0.75rem', marginTop: 8 }}>
                <div className="pd-skel pd-skel-line" style={{ flex: 1, height: 48, borderRadius: 10 }} />
                <div className="pd-skel pd-skel-line" style={{ flex: 1, height: 48, borderRadius: 10 }} />
              </div>
              <div style={{ marginTop: 12 }}>
                {[100, 75, 90, 60].map((w, i) => (
                  <div key={i} className="pd-skel pd-skel-line" style={{ width: `${w}%`, height: 13, marginBottom: 8 }} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return <div className="page-container"><p>Product not found</p></div>;
  }

  const images = product.images && product.images.length > 0 ? product.images : [{ url: PLACEHOLDER, alt: product.name }];
  const lightboxSlides = images.map((img, idx) => ({
    src: resolveImageUrl(img?.url || img),
    alt: img?.alt || `${product.name} — view ${idx + 1}`,
  }));

  // Use variant pricing — size takes priority (determines price), then color, then base
  const effectivePrice = selectedSizeVariant?.sellingPrice || selectedColorVariant?.sellingPrice || product.sellingPrice;
  const effectiveMrp = selectedSizeVariant?.mrp || selectedColorVariant?.mrp || product.mrp;
  const effectiveStock = selectedSizeVariant?.stock != null ? selectedSizeVariant.stock : product.stock;
  const discount = effectiveMrp > effectivePrice ? Math.round((1 - effectivePrice / effectiveMrp) * 100) : 0;
  const savings = effectiveMrp ? Math.round(effectiveMrp - effectivePrice) : 0;

  // Wire / cable products — support per-meter and per-coil buying
  const isWireProduct = product.unit === 'meter' || product.unit === 'roll' || product.category === 'WIRES_CABLES';
  const coilLength = product.coilLength || 90;
  // Separate stock pools for wire products
  const effectiveCoilStock = selectedSizeVariant?.coilStock != null ? selectedSizeVariant.coilStock : (product.coilStock || 0);
  const meterStock = effectiveStock; // loose meters available
  // Per-coil pricing (often cheaper than per-meter × coilLength)
  const coilPrice = product.coilSellingPrice || (effectivePrice * coilLength);
  const coilMrpPrice = product.coilMrp || (effectiveMrp * coilLength);
  const actualQty = isWireProduct && buyingMode === 'coil' ? quantity * coilLength : quantity;
  const lineTotal = isWireProduct && buyingMode === 'coil'
    ? quantity * coilPrice
    : quantity * effectivePrice;
  const coilSavings = isWireProduct && product.coilSellingPrice
    ? (effectivePrice * coilLength) - product.coilSellingPrice
    : 0;

  // Group variants by type for display
  const sizeVariants = (product.variants || []).filter((v) => v.type === 'size');
  const colorVariants = (product.variants || []).filter((v) => v.type === 'color');

  // Product structured data — lets Google show price, availability and (later)
  // ratings as a rich result. Built from the live product so it stays accurate.
  const productLd = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description: product.description,
    image: images.map((img) => img.url).filter(Boolean),
    sku: product.sku || undefined,
    brand: {
      '@type': 'Brand',
      name: (typeof product.brand === 'string' ? product.brand : product.brand?.name) || 'Ceedees Electricals',
    },
    offers: {
      '@type': 'Offer',
      url: `https://ceedeeselectricals.in/product/${product.slug || product._id || ''}`,
      priceCurrency: 'INR',
      price: effectivePrice,
      availability: 'https://schema.org/InStock',
      itemCondition: 'https://schema.org/NewCondition',
      seller: { '@type': 'Organization', name: 'Ceedees Electricals' },
    },
  };

  return (
    <>
      <Helmet>
        <title>{product.name} - Ceedees Electricals</title>
        <meta name="description" content={product.description} />
        <link rel="canonical" href={`https://ceedeeselectricals.in/product/${product.slug || product._id || ''}`} />
        <meta property="og:type" content="product" />
        <meta property="og:title" content={`${product.name} - Ceedees Electricals`} />
        <meta property="og:description" content={product.description} />
        <meta property="og:image" content={images[0]?.url} />
        <script type="application/ld+json">{JSON.stringify(productLd)}</script>
      </Helmet>

      <div className="product-detail-page">
        <div className="page-header">
          <div className="container">
            <Link to="/shop" className="breadcrumb-link">Shop</Link>
            <span className="breadcrumb-sep">/</span>
            <span className="breadcrumb-current">{product.name}</span>
          </div>
        </div>

        <div className="container product-detail-container">
          <div className="detail-grid">
            {/* Images */}
            <div className="detail-images">
              <button
                type="button"
                className="main-image main-image-button"
                onClick={() => setLightboxOpen(true)}
                aria-label="Open full-size image viewer"
              >
                <img
                  src={resolveImageUrl(images[selectedImage]?.url || images[selectedImage])}
                  alt={product.name}
                  loading="eager"
                  decoding="async"
                />
                {discount > 0 && (
                  <span className="discount-badge-large">{discount}% OFF</span>
                )}
                <span className="zoom-hint">Click to zoom</span>
              </button>
              <div className="thumbnail-gallery">
                {images.map((img, idx) => (
                  <button
                    key={idx}
                    className={`thumbnail ${idx === selectedImage ? 'active' : ''}`}
                    onClick={() => setSelectedImage(idx)}
                  >
                    <img
                      src={resolveImageUrl(img?.url || img)}
                      alt={`View ${idx + 1}`}
                      loading="lazy"
                      decoding="async"
                    />
                  </button>
                ))}
              </div>
            </div>

            {/* Details */}
            <div className="detail-info">
              <div className="detail-header">
                <span className="brand-badge">{product.brand}</span>
                <h1>{product.name}</h1>
                <div className="rating-section">
                  <div className="rating">
                    {[...Array(5)].map((_, i) => (
                      <FiStar
                        key={i}
                        className={i < (product.averageRating || 4) ? 'filled' : ''}
                      />
                    ))}
                  </div>
                  <span className="review-count">({product.reviewCount || 0} reviews)</span>
                </div>
              </div>

              {/* Price Section */}
              <div className="price-section">
                <div className="price-display">
                  <span className="current-price">₹{effectivePrice?.toLocaleString('en-IN')}</span>
                  {isWireProduct && <span className="price-per-unit">/meter</span>}
                  {effectiveMrp > effectivePrice && (
                    <>
                      <span className="original-price">₹{effectiveMrp?.toLocaleString('en-IN')}</span>
                      <span className="savings">Save ₹{savings?.toLocaleString('en-IN')}</span>
                    </>
                  )}
                </div>
              </div>

              {/* Product Info */}
              <div className="product-meta">
                <div className="meta-item">
                  <strong>SKU:</strong>
                  <span>{product.sku}</span>
                </div>
                {product.hsnCode && (
                  <div className="meta-item">
                    <strong>HSN Code:</strong>
                    <span>{product.hsnCode}</span>
                  </div>
                )}
                {isWireProduct ? (
                  <div className="meta-item wire-stock-display">
                    <strong>Stock:</strong>
                    <div className="wire-stock-pools">
                      <span className={effectiveCoilStock > 0 ? 'in-stock' : 'out-stock'}>
                        {effectiveCoilStock > 0
                          ? `${effectiveCoilStock} coil${effectiveCoilStock !== 1 ? 's' : ''} (${coilLength}m each)`
                          : 'No coils available'}
                      </span>
                      <span className="wire-stock-divider">|</span>
                      <span className={meterStock > 0 ? 'in-stock' : 'out-stock'}>
                        {meterStock > 0
                          ? `${meterStock} meters loose`
                          : 'No loose meters'}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="meta-item">
                    <strong>Stock:</strong>
                    <span className={effectiveStock > 0 ? 'in-stock' : 'out-stock'}>
                      {effectiveStock > 0
                        ? `${effectiveStock} units in stock`
                        : 'Out of stock'}
                    </span>
                  </div>
                )}
              </div>

              {/* Size / Specification Variants (e.g. 1sqmm, 2sqmm for wires; 1200mm for fans) */}
              {sizeVariants.length > 0 && (
                <div className="variant-section">
                  <label className="variant-label">
                    Size: <strong>{selectedSizeVariant ? selectedSizeVariant.label : 'Select'}</strong>
                  </label>
                  <div className="variant-options">
                    {sizeVariants.map((v, idx) => (
                      <button
                        key={idx}
                        type="button"
                        className={`variant-chip ${selectedSizeVariant === v ? 'active' : ''}`}
                        onClick={() => setSelectedSizeVariant(v)}
                      >
                        <span className="variant-chip-label">{v.label}</span>
                        {v.sellingPrice && (
                          <span className="variant-chip-price">₹{v.sellingPrice?.toLocaleString('en-IN')}</span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Color Variants */}
              {(product.colors?.length > 0 || colorVariants.length > 0) && (
                <div className="color-section">
                  <label className="color-label">
                    Color: <strong>{selectedColor || 'Select a colour'}</strong>
                    {/* Show price next to selected color if it has variant pricing */}
                    {selectedColorVariant?.sellingPrice && (
                      <span className="color-price-tag"> — ₹{selectedColorVariant.sellingPrice?.toLocaleString('en-IN')}</span>
                    )}
                  </label>
                  <div className="color-swatches">
                    {(colorVariants.length > 0
                      ? colorVariants.map((v) => v.label)
                      : product.colors
                    ).map((color) => {
                      const cv = colorVariants.find((v) => v.label === color);
                      return (
                        <button
                          key={color}
                          type="button"
                          title={cv ? `${color} — ₹${cv.sellingPrice?.toLocaleString('en-IN')}` : color}
                          className={`color-swatch ${selectedColor === color ? 'active' : ''}`}
                          style={{ backgroundColor: COLOR_MAP[color] || color }}
                          onClick={() => setSelectedColor(color)}
                          aria-label={color}
                        />
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Quantity Selector */}
              {isWireProduct ? (
                <div className="quantity-section wire-quantity-section">
                  {/* Mode toggle: Meters vs Coil */}
                  <div className="unit-mode-toggle">
                    <button
                      type="button"
                      className={`unit-btn ${buyingMode === 'meter' ? 'active' : ''}`}
                      onClick={() => { setBuyingMode('meter'); setQuantity(1); }}
                    >
                      📏 Per Meter
                    </button>
                    <button
                      type="button"
                      className={`unit-btn ${buyingMode === 'coil' ? 'active' : ''}`}
                      onClick={() => { setBuyingMode('coil'); setQuantity(1); }}
                    >
                      🔄 Coil ({coilLength}m)
                      {coilSavings > 0 && <span className="coil-save-tag">Save ₹{coilSavings.toLocaleString('en-IN')}</span>}
                    </button>
                  </div>

                  {buyingMode === 'meter' ? (
                    <div className="meter-buy-group">
                      <label>How many meters? <span className="stock-hint">({meterStock}m available)</span></label>
                      <div className="meter-presets">
                        {[5, 10, 15, 25, 50, coilLength].filter(m => m <= meterStock).map((m) => (
                          <button
                            key={m}
                            type="button"
                            className={`meter-chip ${quantity === m ? 'active' : ''}`}
                            onClick={() => setQuantity(m)}
                          >
                            {m}m
                          </button>
                        ))}
                      </div>
                      <div className="quantity-selector">
                        <button onClick={() => setQuantity(Math.max(1, quantity - 1))} disabled={quantity <= 1}>
                          <FiMinus />
                        </button>
                        <input
                          type="number"
                          value={quantity}
                          onChange={(e) => {
                            const v = parseInt(e.target.value) || 1;
                            setQuantity(Math.min(Math.max(1, v), meterStock));
                          }}
                          min="1"
                          max={meterStock}
                        />
                        <button
                          onClick={() => setQuantity(Math.min(quantity + 1, meterStock))}
                          disabled={quantity >= meterStock}
                        >
                          <FiPlus />
                        </button>
                        <span className="unit-suffix">meters</span>
                      </div>
                    </div>
                  ) : (
                    <div className="coil-buy-group">
                      <label>How many coils? <span className="coil-info">({coilLength}m each)</span> <span className="stock-hint">({effectiveCoilStock} available)</span></label>
                      <div className="quantity-selector">
                        <button onClick={() => setQuantity(Math.max(1, quantity - 1))} disabled={quantity <= 1}>
                          <FiMinus />
                        </button>
                        <input
                          type="number"
                          value={quantity}
                          onChange={(e) => {
                            const v = parseInt(e.target.value) || 1;
                            setQuantity(Math.min(Math.max(1, v), effectiveCoilStock));
                          }}
                          min="1"
                          max={effectiveCoilStock}
                        />
                        <button
                          onClick={() => setQuantity(quantity + 1)}
                          disabled={quantity >= effectiveCoilStock}
                        >
                          <FiPlus />
                        </button>
                        <span className="unit-suffix">coil{quantity > 1 ? 's' : ''}</span>
                      </div>
                    </div>
                  )}

                  {/* Running total */}
                  <div className="wire-total-bar">
                    <div className="wire-total-left">
                      <span className="wire-total-meters">{actualQty} meters</span>
                      {buyingMode === 'coil' && <span className="wire-total-coils">({quantity} coil{quantity > 1 ? 's' : ''} × ₹{coilPrice.toLocaleString('en-IN')})</span>}
                    </div>
                    <div className="wire-total-right">
                      {buyingMode === 'coil' && coilSavings > 0 && (
                        <span className="wire-total-mrp">₹{(quantity * effectivePrice * coilLength).toLocaleString('en-IN')}</span>
                      )}
                      <span className="wire-total-price">₹{lineTotal.toLocaleString('en-IN')}</span>
                    </div>
                  </div>

                  {actualQty >= 50 && (
                    <a
                      href={`https://wa.me/919447672888?text=Hi%2C%20I%20want%20to%20buy%20*${actualQty}%20meters*%20of%20*${encodeURIComponent(product.name)}*.%20Can%20I%20get%20bulk%20pricing%3F`}
                      target="_blank" rel="noopener noreferrer"
                      className="bulk-qty-hint"
                    >
                      💰 Buying {actualQty}m? Tap for wholesale price!
                    </a>
                  )}
                </div>
              ) : (
                <div className="quantity-section">
                  <label>Quantity:</label>
                  <div className="quantity-selector">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      disabled={quantity <= 1}
                    >
                      <FiMinus />
                    </button>
                    <input type="number" value={quantity} readOnly min="1" />
                    <button
                      onClick={() => setQuantity(quantity + 1)}
                      disabled={quantity >= effectiveStock}
                    >
                      <FiPlus />
                    </button>
                  </div>
                  {quantity >= 5 && (
                    <a
                      href={`https://wa.me/919447672888?text=Hi%2C%20I%20want%20to%20buy%20*${quantity}%20units*%20of%20*${encodeURIComponent(product.name)}*.%20Can%20I%20get%20bulk%20pricing%3F`}
                      target="_blank" rel="noopener noreferrer"
                      className="bulk-qty-hint"
                    >
                      💰 Buying {quantity}+ units? Tap for better price!
                    </a>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="detail-actions">
                <button
                  className="btn btn-primary btn-lg"
                  onClick={handleAddToCart}
                  disabled={
                    addingToCart ||
                    (isWireProduct && buyingMode === 'coil' ? effectiveCoilStock <= 0 : effectiveStock <= 0)
                  }
                  style={{ opacity: addingToCart ? 0.75 : 1, transition: 'opacity 0.2s' }}
                >
                  {addingToCart ? '⏳ Adding…' : 'Add to Cart'}
                </button>
                <button
                  className={`btn btn-outline btn-lg ${product && isInWishlist(product._id) ? 'wishlisted-btn' : ''}`}
                  onClick={() => product && toggleWishlist(product._id, product.name)}
                >
                  <FiHeart style={product && isInWishlist(product._id) ? { fill: '#e53e3e', color: '#e53e3e' } : {}} />
                  {product && isInWishlist(product._id) ? 'Wishlisted' : 'Wishlist'}
                </button>
              </div>

              {/* Delivery Checker */}
              <div className="delivery-checker-section">
                <h4>Check Delivery Availability</h4>
                <PincodeChecker
                  weightGrams={(product.weight || 500) * actualQty}
                  orderTotal={effectivePrice * actualQty}
                />
              </div>

              {/* Bulk Order Callout */}
              <div className="bulk-callout">
                <div className="bulk-callout-icon">🏷️</div>
                <div className="bulk-callout-text">
                  <strong>Bulk / Contractor Pricing Available</strong>
                  <span>Buying 5+ units? Get dealer/wholesale rates.</span>
                </div>
                <a
                  href={`https://wa.me/919447672888?text=Hi%2C%20I'm%20interested%20in%20buying%20*${encodeURIComponent(product.name)}*%20in%20bulk.%20Please%20share%20wholesale%20pricing.`}
                  target="_blank" rel="noopener noreferrer"
                  className="bulk-callout-btn"
                >
                  💬 WhatsApp Us
                </a>
              </div>

              {/* Trust Strip */}
              <div className="pd-trust-strip">
                <div className="pd-trust-item">
                  <span className="pd-ti-icon">🚚</span>
                  <div>
                    <strong>Free Delivery</strong>
                    <span>Orders above ₹2,000</span>
                  </div>
                </div>
                <div className="pd-trust-item">
                  <span className="pd-ti-icon">📄</span>
                  <div>
                    <strong>GST Invoice</strong>
                    <span>With HSN codes</span>
                  </div>
                </div>
                <div className="pd-trust-item">
                  <span className="pd-ti-icon">✅</span>
                  <div>
                    <strong>100% Original</strong>
                    <span>ISI certified brands</span>
                  </div>
                </div>
                <div className="pd-trust-item">
                  <span className="pd-ti-icon">🔄</span>
                  <div>
                    <strong>{product.isReturnable === false ? 'Not Returnable' : 'Easy Returns'}</strong>
                    <span>{product.isReturnable === false ? 'See note below' : '7-day hassle-free'}</span>
                  </div>
                </div>
              </div>

              {/* Non-returnable notice — visible upfront so customers know
                  before they buy, not after they ask for a refund. */}
              {product.isReturnable === false && (
                <div style={{ marginTop: '14px', padding: '12px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px', display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                  <span style={{ fontSize: '1.3rem' }}>⚠️</span>
                  <div>
                    <strong style={{ color: '#991b1b', display: 'block', marginBottom: '2px' }}>This item cannot be returned</strong>
                    <span style={{ fontSize: '0.85rem', color: '#7f1d1d' }}>
                      {product.nonReturnableReason || 'Due to the nature of this product, returns are not accepted. Please verify your requirements before ordering.'}
                    </span>
                  </div>
                </div>
              )}

              {/* Structured warranty badge (if set) */}
              {product.warrantyMonths > 0 && (
                <div style={{ marginTop: '12px', padding: '10px 14px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '10px', display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <span style={{ fontSize: '1.2rem' }}>🛡️</span>
                  <div style={{ fontSize: '0.88rem', color: '#1e40af' }}>
                    <strong>{product.warrantyMonths >= 12
                      ? `${(product.warrantyMonths / 12).toFixed(product.warrantyMonths % 12 === 0 ? 0 : 1)} year`
                      : `${product.warrantyMonths} month`} warranty</strong>
                    {' '}— {product.warrantyType === 'seller' ? 'Handled by Ceedees' : product.warrantyType === 'manufacturer' ? `Handled by ${product.brand || 'the manufacturer'}` : ''}
                  </div>
                </div>
              )}

              {/* Amazon-style "About this item" highlights */}
              {product.highlights && product.highlights.length > 0 && (
                <div className="highlights-section">
                  <h3>About this item</h3>
                  <ul className="highlights-list">
                    {product.highlights.map((point, idx) => (
                      <li key={idx}>{point}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Description */}
              <div className="description-section">
                <h3>Description</h3>
                <p>{product.description}</p>
              </div>
            </div>
          </div>

          {/* Product Information — Amazon-style detailed specs table */}
          {(product.technicalDetails?.length > 0 || product.manufacturer || product.warranty) && (
            <div className="product-info-section">
              <h2>Product Information</h2>
              <div className="product-info-grid">
                {/* Quick facts from model fields */}
                <table className="specs-table product-info-table">
                  <tbody>
                    {product.manufacturer && (
                      <tr><td className="spec-label">Manufacturer</td><td className="spec-value">{product.manufacturer}</td></tr>
                    )}
                    {product.brand && (
                      <tr><td className="spec-label">Brand</td><td className="spec-value">{product.brand}</td></tr>
                    )}
                    {product.modelNumber && (
                      <tr><td className="spec-label">Model Number</td><td className="spec-value">{product.modelNumber}</td></tr>
                    )}
                    {product.partNumber && (
                      <tr><td className="spec-label">Part Number</td><td className="spec-value">{product.partNumber}</td></tr>
                    )}
                    {product.countryOfOrigin && (
                      <tr><td className="spec-label">Country of Origin</td><td className="spec-value">{product.countryOfOrigin}</td></tr>
                    )}
                    {product.includedComponents && (
                      <tr><td className="spec-label">Included Components</td><td className="spec-value">{product.includedComponents}</td></tr>
                    )}
                    {product.warranty && (
                      <tr><td className="spec-label">Warranty</td><td className="spec-value">{product.warranty}</td></tr>
                    )}
                    {product.weight && (
                      <tr><td className="spec-label">Weight</td><td className="spec-value">{product.weight >= 1000 ? `${(product.weight / 1000).toFixed(1)} kg` : `${product.weight} g`}</td></tr>
                    )}
                    {product.hsnCode && (
                      <tr><td className="spec-label">HSN Code</td><td className="spec-value">{product.hsnCode}</td></tr>
                    )}
                  </tbody>
                </table>

                {/* Technical details grouped by section */}
                {product.technicalDetails?.length > 0 && (() => {
                  const sections = {};
                  product.technicalDetails.forEach((td) => {
                    const s = td.section || 'General';
                    if (!sections[s]) sections[s] = [];
                    sections[s].push(td);
                  });
                  return Object.entries(sections).map(([section, items]) => (
                    <div key={section} className="tech-detail-block">
                      <h4 className="tech-detail-heading">{section}</h4>
                      <table className="specs-table product-info-table">
                        <tbody>
                          {items.map((item, idx) => (
                            <tr key={idx}>
                              <td className="spec-label">{item.key}</td>
                              <td className="spec-value">{item.value}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ));
                })()}
              </div>
            </div>
          )}

          {/* Specifications Table (legacy / simple specs) */}
          {product.specifications && product.specifications.length > 0 && !(product.technicalDetails?.length > 0) && (
            <div className="specifications-section">
              <h2>Specifications</h2>
              <table className="specs-table">
                <tbody>
                  {product.specifications.map((spec, idx) => (
                    <tr key={idx}>
                      <td className="spec-label">{spec.key}</td>
                      <td className="spec-value">{spec.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Reviews Section */}
          {product.reviews && product.reviews.length > 0 && (
            <div className="reviews-section">
              <h2>Customer Reviews</h2>
              <div className="reviews-list">
                {product.reviews.map((review, idx) => (
                  <div key={idx} className="review-item">
                    <div className="review-header">
                      <strong>{review.author}</strong>
                      <div className="review-rating">
                        {[...Array(review.rating)].map((_, i) => (
                          <FiStar key={i} className="filled" />
                        ))}
                      </div>
                    </div>
                    <p>{review.comment}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Frequently Bought Together */}
          {relatedProducts.filter(p => p.slug).length >= 2 && (
            <div className="fbt-section">
              <h2 className="fbt-title">Frequently Bought Together</h2>
              <div className="fbt-layout">
                <div className="fbt-products">
                  {/* Current product */}
                  <div className="fbt-item fbt-main">
                    <div className="fbt-img-wrap">
                      <img src={primaryImageUrl(product)} alt={product.name} />
                    </div>
                    <div className="fbt-item-info">
                      <span className="fbt-brand">{product.brand}</span>
                      <p className="fbt-name">{product.name}</p>
                      <span className="fbt-price">₹{product.sellingPrice?.toLocaleString('en-IN')}</span>
                    </div>
                  </div>

                  {relatedProducts.filter(p => p.slug).slice(0, 2).map((rp) => (
                    <React.Fragment key={rp._id}>
                      <div className="fbt-plus">+</div>
                      <Link to={`/product/${rp.slug}`} className="fbt-item">
                        <div className="fbt-img-wrap">
                          <img src={primaryImageUrl(rp)} alt={rp.name} loading="lazy" />
                        </div>
                        <div className="fbt-item-info">
                          <span className="fbt-brand">{rp.brand}</span>
                          <p className="fbt-name">{rp.name}</p>
                          <span className="fbt-price">₹{rp.sellingPrice?.toLocaleString('en-IN')}</span>
                        </div>
                      </Link>
                    </React.Fragment>
                  ))}
                </div>

                <div className="fbt-cta">
                  <div className="fbt-total-row">
                    <span>Total price:</span>
                    <strong>₹{(
                      product.sellingPrice +
                      relatedProducts.filter(p => p.slug).slice(0, 2).reduce((s, p) => s + (p.sellingPrice || 0), 0)
                    ).toLocaleString('en-IN')}</strong>
                  </div>
                  <p className="fbt-save">for all 3 items (excl. GST)</p>
                  <button
                    className="fbt-add-btn"
                    onClick={async () => {
                      try {
                        await addToCart(product._id, 1);
                        for (const rp of relatedProducts.filter(p => p.slug).slice(0, 2)) {
                          await addToCart(rp._id, 1);
                        }
                        toast.success('All 3 items added to cart!');
                      } catch {
                        toast.error('Failed to add some items');
                      }
                    }}
                  >
                    Add All 3 to Cart
                  </button>
                  <a
                    href={`https://wa.me/919447672888?text=Hi%2C%20I%20want%20to%20buy%20these%20together%3A%20*${encodeURIComponent(product.name)}*%20and%20related%20items.%20Do%20you%20have%20a%20combo%20offer%3F`}
                    target="_blank" rel="noopener noreferrer"
                    className="fbt-whatsapp-btn"
                  >
                    💬 Ask for Combo Offer
                  </a>
                </div>
              </div>
            </div>
          )}

          {/* Related Products */}
          {relatedProducts.length > 0 && (
            <div className="related-section">
              <h2>Customers Also Viewed</h2>
              <div className="products-grid-related">
                {relatedProducts.filter(p => p.slug).map((relProduct) => (
                  <Link
                    key={relProduct._id}
                    to={`/product/${relProduct.slug}`}
                    className="product-card"
                  >
                    <div className="product-image">
                      <img src={primaryImageUrl(relProduct)} alt={relProduct.name} loading="lazy" decoding="async" />
                      {relProduct.mrp > relProduct.sellingPrice && (
                        <span className="rel-discount-badge">
                          {Math.round(((relProduct.mrp - relProduct.sellingPrice) / relProduct.mrp) * 100)}% OFF
                        </span>
                      )}
                    </div>
                    <div className="product-info">
                      <p className="brand">{relProduct.brand}</p>
                      <h4>{relProduct.name}</h4>
                      <div className="product-price">
                        <span className="price">₹{relProduct.sellingPrice?.toLocaleString('en-IN')}</span>
                        {relProduct.mrp > relProduct.sellingPrice && (
                          <span className="mrp">₹{relProduct.mrp?.toLocaleString('en-IN')}</span>
                        )}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <ImageLightbox
        open={lightboxOpen}
        slides={lightboxSlides}
        startIndex={selectedImage}
        onIndexChange={setSelectedImage}
        onClose={() => setLightboxOpen(false)}
      />
    </>
  );
}

export default ProductDetail;
